import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { setAtcBlock, ATC_BLOCK } from '@theme/atc-gate';

/**
 * @theme/logic-metafields, loaded lazily — only when the server rendered the builder in
 * its non-English loading state (`data-logic-loading`), so English pages never load it.
 */
const loadLogicMetafields = () => import('@theme/logic-metafields');

/** The variant metafield that decides single vs combination (see snippets/logic-metafields-keys.liquid). */
const SELECTION_TYPE_PATH = 'ediblearrangement.cyo_selection_type';

/**
 * CYO (Create Your Own) Builder Web Component.
 *
 * Manages flavor/topping selection for CYO products and keeps the gallery
 * preview slots in sync. Two selection modes, driven by the variant metafield
 * `cyo_selection_type` (exposed as `data-selection-type`):
 *
 * - `single`: one image per slot. All step selections are distributed evenly
 *   across the slots (order preserved).
 * - `combination`: each slot stacks two images — a bottom (step 1) and a top
 *   (step 2). Each layer distributes evenly across the slots. Until a step-2
 *   item is picked, the top mirrors the bottom.
 *
 * On variant change it re-renders itself from the fetched page HTML (matching
 * by `data-block-id`, like `variant-rich-text-metafield`) and clears the
 * selection — it never touches the variant picker.
 *
 * It also adds (and only ever removes) its own "no valid selection" disabled
 * state on the Add to Cart button; it never force-enables a natively disabled
 * (sold out / unavailable) button.
 *
 * @element cyo-builder
 */
class CyoBuilder extends Component {
  /** @type {Map<string, Array<{id: string, image: string, title: string}>>} */
  #selections = new Map();

  /** @type {number} */
  #totalSlots = 0;

  /** Per-step slot quantity (how many slots each step fills), keyed by step id.
   * @type {Map<string, number>} */
  #stepQuantities = new Map();

  /** @type {'single' | 'combination'} */
  #selectionType = 'single';

  /** Item word forms (canonical casing) for the cart display. @type {string} */
  #cyoTypeSingular = '';
  #cyoTypePlural = '';

  /** Gift-wrap selection (its own channel — never a step, so it stays out of
   * #selections / slots / segments / count / ATC). Single-select: exactly one is
   * always chosen when the section is shown, and it can't be unselected.
   * @type {{ variantId: string } | null} */
  #giftWrapSelection = null;

  /** Controller for child-control listeners; recreated on each (re)init. @type {AbortController | null} */
  #controller = null;

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');

    // Global listeners persist across variant-change morphs (not tied to the
    // per-init controller, since morph replaces only the child controls).
    this.#section?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#section?.addEventListener('click', this.#handleSectionClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    window.addEventListener('resize', this.#updateAllArrows);

    this.#initOrResolve();
  }

  /** Bumped per resolution, so a stale one (variant swapped meanwhile) is dropped. */
  #logicSeq = 0;

  /**
   * English pages: init straight away (Liquid already compared English values).
   * Non-English pages (`data-logic-loading`): resolve the English selection type first.
   */
  #initOrResolve() {
    if (this.dataset.logicLoading) {
      this.#resolveSelectionTypeThenInit();
      return;
    }
    this.#init();
  }

  /**
   * Non-English locales: Translate & Adapt translates `cyo_selection_type`, so the server
   * rendered the neutral superset ('single', every step, steps inert). Read the English
   * value for the current variant (one cached request per product, retried once), apply
   * it exactly as the Liquid would, then run the normal init. On final failure the steps
   * stay inert and Add to Cart stays in its existing CYO "no valid selection" disabled
   * state; the next variant change tries again.
   */
  async #resolveSelectionTypeThenInit() {
    const seq = ++this.#logicSeq;
    this.dataset.logicLoading = 'true';
    this.setAttribute('aria-busy', 'true');
    // No box can be valid while the mode is unknown — hold Add to Cart closed.
    for (const btn of this.#getAtcButtons()) setAtcBlock(btn, ATC_BLOCK.cyo, true);
    try {
      const { needsEnglishValues, getVariantValue } = await loadLogicMetafields();
      const raw = needsEnglishValues()
        ? await getVariantValue(this.dataset.productHandle || '', this.dataset.variantId || '', SELECTION_TYPE_PATH)
        : this.dataset.selectionType;
      if (seq !== this.#logicSeq || !this.isConnected) return;
      this.#applySelectionType(raw);
      this.#clearLogicLoading();
      this.#init();
    } catch {
      if (seq !== this.#logicSeq || !this.isConnected) return;
      this.dataset.logicLoading = 'error';
      this.setAttribute('aria-busy', 'false');
    }
  }

  /**
   * Normalise the raw metafield value the way blocks/_cyo-builder.liquid does —
   * 'combination' only with two or more step collections, otherwise 'single' — and
   * reflect it on the builder and the gallery preview. A combination box stacks exactly
   * the first two steps, so any further steps (rendered as part of the neutral
   * superset) are removed.
   * @param {unknown} raw
   */
  #applySelectionType(raw) {
    const value = String(raw ?? '').trim().toLowerCase() || 'single';
    const steps = [...this.querySelectorAll('[data-cyo-step]')];
    const stepCount = parseInt(this.dataset.stepCount, 10) || steps.length;
    const mode = value === 'combination' && stepCount >= 2 ? 'combination' : 'single';
    if (mode === 'combination') {
      for (const step of steps.slice(2)) step.remove();
    }
    this.dataset.selectionType = mode;

    const preview = (this.#section || document).querySelector('[data-cyo-preview]');
    if (preview instanceof HTMLElement) {
      preview.dataset.selectionType = mode;
      delete preview.dataset.logicLoading;
    }
  }

  /** Leave the logic-loading state: steps become interactive again. */
  #clearLogicLoading() {
    delete this.dataset.logicLoading;
    this.removeAttribute('aria-busy');
    for (const step of this.querySelectorAll('[data-logic-inert]')) {
      step.removeAttribute('inert');
      step.removeAttribute('data-logic-inert');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#section?.removeEventListener('click', this.#handleSectionClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    window.removeEventListener('resize', this.#updateAllArrows);
    this.#controller?.abort();
    this.#controller = null;
    this.#section = null;
  }

  /**
   * (Re)initialise from the current DOM: read config, bind child controls, and
   * reset selection state. Safe to call again after a variant-change morph.
   */
  #init() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#totalSlots = parseInt(this.dataset.totalSlots, 10) || 16;
    this.#selectionType = this.dataset.selectionType === 'combination' ? 'combination' : 'single';
    this.#cyoTypeSingular = this.dataset.cyoTypeSingular || '';
    this.#cyoTypePlural = this.dataset.cyoTypePlural || '';
    this.#selections.clear();
    this.#stepQuantities.clear();

    this.#bindStepHeaders(signal);
    this.#bindCards(signal);
    this.#bindScrollers(signal);
    this.#initGiftWrap(signal);

    this.#applyCardStates();
    this.#updatePreview();
    this.#applyAtcState();
    this.#updateAllArrows();
    this.#serializeCart();
  }

  /**
   * Toggle collapsible step sections via aria-expanded.
   *
   * @param {AbortSignal} signal
   */
  #bindStepHeaders(signal) {
    const headers = this.querySelectorAll('[data-cyo-step-header]');
    for (const header of headers) {
      header.addEventListener(
        'click',
        () => {
          const expanded = header.getAttribute('aria-expanded') === 'true';
          header.setAttribute('aria-expanded', String(!expanded));
          const content = header
            .closest('[data-cyo-step], [data-cyo-gift-wrap]')
            ?.querySelector('[data-cyo-step-content]');
          if (content) {
            content.setAttribute('aria-hidden', String(expanded));
          }
          // Arrow visibility depends on measurable widths, so recompute once expanded.
          requestAnimationFrame(() => this.#updateAllArrows());
        },
        { signal }
      );
    }
  }

  /**
   * Bind click handlers to all selectable product cards, and record each step's
   * selection limit / slot quantity.
   *
   * @param {AbortSignal} signal
   */
  #bindCards(signal) {
    const steps = this.querySelectorAll('[data-cyo-step]');
    for (const step of steps) {
      const stepId = step.dataset.cyoStep || '';
      const limit = parseInt(step.dataset.selectionLimit, 10) || 4;

      if (!this.#selections.has(stepId)) {
        this.#selections.set(stepId, []);
      }
      // How many slots this step fills (falls back to the full box).
      this.#stepQuantities.set(stepId, parseInt(step.dataset.stepQuantity, 10) || this.#totalSlots);

      const cards = step.querySelectorAll('[data-cyo-card]');
      for (const card of cards) {
        card.addEventListener('click', () => this.#toggleCard(stepId, card, limit), { signal });
      }
    }
  }

  /**
   * Bind the horizontal-scroll "previous"/"next" arrows for each step's card row.
   *
   * @param {AbortSignal} signal
   */
  #bindScrollers(signal) {
    const scrollers = this.querySelectorAll('.cyo-builder__scroller');
    for (const scroller of scrollers) {
      const cards = scroller.querySelector('[data-cyo-cards]');
      const prev = scroller.querySelector('[data-cyo-scroll-prev]');
      const next = scroller.querySelector('[data-cyo-scroll-next]');
      if (!cards) continue;

      if (prev) {
        prev.addEventListener(
          'click',
          () => cards.scrollBy({ left: cards.clientWidth * -0.85, behavior: 'smooth' }),
          { signal }
        );
      }

      if (next) {
        next.addEventListener(
          'click',
          () => cards.scrollBy({ left: cards.clientWidth * 0.85, behavior: 'smooth' }),
          { signal }
        );
      }

      cards.addEventListener('scroll', () => this.#updateArrows(cards, prev, next), { signal });
    }
  }

  /**
   * Show each arrow only when the row is scrollable in that direction: previous
   * when not at the start, next when not at the end.
   *
   * @param {Element} cards
   * @param {Element | null} prev
   * @param {Element | null} next
   */
  #updateArrows(cards, prev, next) {
    const scrollable = cards.scrollWidth > cards.clientWidth + 1;
    const atStart = cards.scrollLeft <= 1;
    const atEnd = cards.scrollLeft + cards.clientWidth >= cards.scrollWidth - 1;
    const prevVisible = scrollable && !atStart;
    const nextVisible = scrollable && !atEnd;

    // Hiding an arrow sets display:none, which drops keyboard focus to <body>. If
    // the focused arrow is about to hide at a boundary, hand focus to the opposite
    // arrow (which becomes visible at that boundary) so keyboard nav isn't lost.
    const active = document.activeElement;
    const prevHiding = prev && active === prev && !prevVisible;
    const nextHiding = next && active === next && !nextVisible;

    if (prev) prev.classList.toggle('is-visible', prevVisible);
    if (next) next.classList.toggle('is-visible', nextVisible);

    if (prevHiding && nextVisible) next.focus();
    else if (nextHiding && prevVisible) prev.focus();
  }

  /**
   * Recompute arrow visibility for every step.
   *
   * @type {EventListener}
   */
  #updateAllArrows = () => {
    const scrollers = this.querySelectorAll('.cyo-builder__scroller');
    for (const scroller of scrollers) {
      const cards = scroller.querySelector('[data-cyo-cards]');
      const prev = scroller.querySelector('[data-cyo-scroll-prev]');
      const next = scroller.querySelector('[data-cyo-scroll-next]');
      if (cards) this.#updateArrows(cards, prev, next);
    }
  };

  /**
   * Bind the gift-wrap cards (its own channel, not steps) and set the default
   * selection. No-op when the variant has no gift-wrap section.
   *
   * @param {AbortSignal} signal
   */
  #initGiftWrap(signal) {
    const section = this.querySelector('[data-cyo-gift-wrap]');
    if (!section) {
      this.#giftWrapSelection = null;
      return;
    }
    for (const card of section.querySelectorAll('[data-cyo-gift-wrap-card]')) {
      card.addEventListener('click', () => this.#selectGiftWrap(section, card), { signal });
    }
    // Default to the first card (matches the always-one-selected requirement).
    this.#resetGiftWrap();
  }

  /**
   * Single-select a gift-wrap card. Required selection — clicking the already
   * selected card is a no-op (it can't be unselected); clicking another switches
   * to it. Gift wrap doesn't affect the preview or the ATC gate, so this only
   * re-serializes the cart payload.
   *
   * @param {Element} section
   * @param {Element} card
   */
  #selectGiftWrap(section, card) {
    if (card.getAttribute('aria-pressed') === 'true') return;
    for (const other of section.querySelectorAll('[data-cyo-gift-wrap-card]')) {
      other.setAttribute('aria-pressed', other === card ? 'true' : 'false');
    }
    this.#giftWrapSelection = { variantId: card.dataset.variantId || '' };
    this.#serializeCart();
  }

  /**
   * Reset the gift-wrap channel to its default (first card pressed), or clear it
   * when the current variant has no gift-wrap section. Does not re-serialize on
   * its own — callers (#initGiftWrap, #clearSelections) serialize afterwards.
   */
  #resetGiftWrap() {
    const section = this.querySelector('[data-cyo-gift-wrap]');
    if (!section) {
      this.#giftWrapSelection = null;
      return;
    }
    const cards = section.querySelectorAll('[data-cyo-gift-wrap-card]');
    const first = cards[0] || null;
    for (const card of cards) {
      card.setAttribute('aria-pressed', card === first ? 'true' : 'false');
    }
    this.#giftWrapSelection = first ? { variantId: first.dataset.variantId || '' } : null;
  }

  /**
   * Toggle a card's selected state.
   *
   * @param {string} stepId
   * @param {Element} card
   * @param {number} limit
   */
  #toggleCard(stepId, card, limit) {
    // Locked / limit-faded cards are non-interactive.
    if (card.getAttribute('data-cyo-disabled') === 'true') return;

    const selections = this.#selections.get(stepId) || [];
    const productId = card.dataset.productId;
    const existingIndex = selections.findIndex((s) => s.id === productId);

    if (existingIndex >= 0) {
      // Deselect
      selections.splice(existingIndex, 1);
      card.setAttribute('aria-pressed', 'false');
    } else {
      // Respect the per-step selection limit
      if (selections.length >= limit) return;

      selections.push({
        id: productId,
        variantId: card.dataset.variantId || '',
        image: card.dataset.productImage || '',
        title: card.dataset.productTitle || '',
      });
      card.setAttribute('aria-pressed', 'true');
    }

    this.#selections.set(stepId, selections);
    this.#enforceDependency();
    this.#applyCardStates();
    this.#updatePreview();
    this.#applyAtcState();
    this.#dispatchChange();
  }

  /**
   * Combination dependency: steps after the first require a first-step
   * selection. If the first step is empty, clear every later step's selections
   * (and their pressed state) so nothing lingers in an invalid state.
   */
  #enforceDependency() {
    if (this.#selectionType !== 'combination') return;
    const steps = [...this.querySelectorAll('[data-cyo-step]')];
    const firstStepId = steps[0]?.dataset.cyoStep || '';
    if ((this.#selections.get(firstStepId) || []).length > 0) return;

    for (const step of steps.slice(1)) {
      const stepId = step.dataset.cyoStep || '';
      this.#selections.set(stepId, []);
      for (const card of step.querySelectorAll('[data-cyo-card]')) {
        card.setAttribute('aria-pressed', 'false');
      }
    }
  }

  /**
   * Fade/disable cards that can't be chosen: those in a step at its limit, and —
   * in combination — every step after the first until the first step has a
   * selection.
   */
  #applyCardStates() {
    const steps = [...this.querySelectorAll('[data-cyo-step]')];
    const firstStepId = steps[0]?.dataset.cyoStep || '';
    const firstHasSelection = (this.#selections.get(firstStepId) || []).length > 0;

    steps.forEach((step, index) => {
      const stepId = step.dataset.cyoStep || '';
      const limit = parseInt(step.dataset.selectionLimit, 10) || 4;
      const selections = this.#selections.get(stepId) || [];
      const atLimit = selections.length >= limit;
      const locked = this.#selectionType === 'combination' && index > 0 && !firstHasSelection;

      for (const card of step.querySelectorAll('[data-cyo-card]')) {
        const selected = card.getAttribute('aria-pressed') === 'true';
        if (locked || (atLimit && !selected)) {
          card.setAttribute('data-cyo-disabled', 'true');
        } else {
          card.removeAttribute('data-cyo-disabled');
        }
      }
    });
  }

  /**
   * Reset every selection and the preview, and re-evaluate the ATC gate.
   */
  #clearSelections() {
    for (const [stepId] of this.#selections) {
      this.#selections.set(stepId, []);
    }

    for (const card of this.querySelectorAll('[data-cyo-card]')) {
      card.setAttribute('aria-pressed', 'false');
    }

    // Gift wrap rides its own channel: reset it to the default (or clear it when
    // the current variant has no wrap section). #dispatchChange re-serializes.
    this.#resetGiftWrap();

    this.#applyCardStates();
    this.#updatePreview();
    this.#applyAtcState();
    this.#dispatchChange();
  }

  /**
   * Section-level click delegation for the gallery "Clear Selected" control,
   * which lives outside this element.
   *
   * @type {EventListener}
   */
  #handleSectionClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const clearButton = target?.closest('[data-cyo-clear]');
    if (clearButton) {
      this.#clearSelections();
    }
  };

  /**
   * Distribute a step's selections evenly across the slot quantity it fills.
   * Remainder goes to earliest selections.
   *
   * @param {Array<{id: string, image: string, title: string}>} selections
   * @param {number} quantity - How many slots these selections fill.
   * @returns {Array<{image: string, title: string, count: number}>}
   */
  #distributeSlots(selections, quantity) {
    if (selections.length === 0 || quantity <= 0) return [];

    const count = selections.length;
    const base = Math.floor(quantity / count);
    const remainder = quantity % count;

    return selections.map((sel, i) => ({
      image: sel.image,
      title: sel.title,
      count: i < remainder ? base + 1 : base,
    }));
  }

  /**
   * Concatenate every step's distribution in DOM order (single-mode fill).
   *
   * @returns {Array<{image: string, title: string, count: number}>}
   */
  #buildDistribution() {
    const distribution = [];
    for (const [stepId, selections] of this.#selections) {
      if (selections.length === 0) continue;
      const quantity = this.#stepQuantities.get(stepId) || this.#totalSlots;
      distribution.push(...this.#distributeSlots(selections, quantity));
    }
    return distribution;
  }

  /**
   * Expand a `{image,title,count}[]` distribution into a per-slot array.
   *
   * @param {Array<{image: string, title: string, count: number}>} dist
   * @param {number} slotCount
   * @returns {Array<{image: string, title: string}>}
   */
  #expand(dist, slotCount) {
    const out = [];
    for (const d of dist) {
      for (let i = 0; i < d.count && out.length < slotCount; i++) {
        out.push({ image: d.image, title: d.title });
      }
    }
    return out;
  }

  /**
   * Per-slot array for a single step's selections (used per layer in combination).
   *
   * @param {string | undefined} stepId
   * @param {number} slotCount
   * @returns {Array<{image: string, title: string}>}
   */
  #layerForStep(stepId, slotCount) {
    if (!stepId) return [];
    const selections = this.#selections.get(stepId) || [];
    const quantity = this.#stepQuantities.get(stepId) || slotCount;
    return this.#expand(this.#distributeSlots(selections, quantity), slotCount);
  }

  /**
   * Segmented single-mode fill: each step occupies its own fixed slot range
   * (offset = sum of previous steps' quantities), so an unselected step leaves
   * its segment empty instead of later steps shifting forward. Used for
   * multi-step single boxes (e.g. the Dessert Board's per-collection rows).
   *
   * @param {number} slotCount
   * @returns {Array<{image: string, title: string} | undefined>}
   */
  #buildSegmentedLayer(slotCount) {
    const out = new Array(slotCount);
    let offset = 0;
    for (const [stepId, selections] of this.#selections) {
      const quantity = this.#stepQuantities.get(stepId) || slotCount;
      if (selections.length > 0) {
        const expanded = this.#expand(this.#distributeSlots(selections, quantity), quantity);
        for (let k = 0; k < expanded.length && offset + k < slotCount; k++) {
          out[offset + k] = expanded[k];
        }
      }
      offset += quantity;
    }
    return out;
  }

  /**
   * Whether the box lays out one step per fixed segment: single mode with more
   * than one step (Dessert Board). Combination and single 1-step boxes are not
   * segmented (they keep the packed + smart-organized fill).
   *
   * @returns {boolean}
   */
  #isSegmented() {
    return this.#selectionType !== 'combination' && this.#selections.size > 1;
  }

  /**
   * Build the bottom (+ optional top) per-slot layers for the current mode.
   *
   * @param {number} slotCount
   * @returns {{ bottom: Array<{image: string, title: string}>, top: Array<{image: string, title: string}> }}
   */
  #buildLayers(slotCount) {
    if (this.#selectionType === 'combination') {
      const stepIds = [...this.#selections.keys()];
      const bottom = this.#layerForStep(stepIds[0], slotCount);
      const step2Id = stepIds[1];
      const step2Selected = step2Id ? (this.#selections.get(step2Id) || []).length > 0 : false;
      // Top mirrors the bottom until a step-2 item is chosen.
      const top = step2Selected ? this.#layerForStep(step2Id, slotCount) : bottom.slice();
      return { bottom, top };
    }
    // Multi-step single (Dessert Board): each step fills its own fixed segment.
    if (this.#isSegmented()) {
      return { bottom: this.#buildSegmentedLayer(slotCount), top: [] };
    }
    // Single 1-step: one image per slot, packed, no top layer.
    return { bottom: this.#expand(this.#buildDistribution(), slotCount), top: [] };
  }

  /**
   * Flatten all step selections into a single ordered array.
   *
   * @returns {Array<{id: string, image: string, title: string}>}
   */
  #getAllSelections() {
    const all = [];
    for (const [, selections] of this.#selections) {
      for (const sel of selections) {
        all.push(sel);
      }
    }
    return all;
  }

  /**
   * @returns {boolean} Whether at least one CYO option is selected.
   */
  #hasSelection() {
    for (const [, selections] of this.#selections) {
      if (selections.length > 0) return true;
    }
    return false;
  }

  /**
   * Whether the current selection satisfies the Add to Cart requirement.
   *
   * Segmented single-type boxes (e.g. the Dessert Board — single type with more
   * than one step) require at least one selection in EVERY step. Single 1-step
   * boxes and combination boxes only require one selection overall (combination's
   * second step stays optional, matching the live site).
   *
   * @returns {boolean}
   */
  #isAtcValid() {
    if (this.#isSegmented()) {
      if (this.#selections.size === 0) return false;
      for (const [, selections] of this.#selections) {
        if (selections.length === 0) return false;
      }
      return true;
    }
    return this.#hasSelection();
  }

  /**
   * Every Add to Cart button for this product, scoped to the surrounding section.
   *
   * All of them, not just the first: a template can render a second one (the
   * sticky bar). Gating only the first would leave that one live with no
   * selection — and it is the same set the PDP availability block gates, so the
   * two stay consistent.
   *
   * @returns {NodeListOf<HTMLButtonElement>}
   */
  #getAtcButtons() {
    const root = this.#section || document;
    return root.querySelectorAll('add-to-cart-component button[name="add"]');
  }

  /**
   * Gate the Add to Cart button on a valid CYO selection.
   */
  #applyAtcState() {
    // Through the shared registry: on CYO templates the PDP availability block
    // gates this same button for its own reason. Writing `disabled` directly
    // meant whoever released last won, so completing a selection could open Add
    // to Cart even though the selected Store can't fulfil the product.
    for (const btn of this.#getAtcButtons()) {
      setAtcBlock(btn, ATC_BLOCK.cyo, !this.#isAtcValid());
    }
  }

  /**
   * Show the gallery "Clear Selected" control only when something is selected.
   */
  #applyClearVisibility() {
    const root = this.#section || document;
    const clear = root.querySelector('[data-cyo-clear]');
    if (clear) clear.classList.toggle('is-visible', this.#hasSelection());
  }

  /**
   * Show the selected images in a slot, keeping the background. `top` is only
   * used in combination mode; pass it falsy for single.
   *
   * @param {Element} slot
   * @param {{image: string, title: string}} bottom
   * @param {{image: string, title: string}} [top]
   */
  #fillSlot(slot, bottom, top) {
    const bottomImg = slot.querySelector('.cyo-preview__slot-item--bottom');
    if (bottomImg && bottom) {
      bottomImg.src = bottom.image;
      bottomImg.alt = bottom.title || '';
    }

    const topImg = slot.querySelector('.cyo-preview__slot-item--top');
    if (topImg) {
      if (top) {
        topImg.src = top.image;
        topImg.alt = top.title || '';
      } else {
        topImg.removeAttribute('src');
        topImg.alt = '';
      }
    }

    slot.setAttribute('data-filled', bottom ? 'true' : 'false');
    slot.setAttribute('data-has-top', top ? 'true' : 'false');
  }

  /**
   * Return a slot to its empty state (placeholder icon visible, items hidden).
   *
   * @param {Element} slot
   */
  #emptySlot(slot) {
    for (const cls of ['.cyo-preview__slot-item--bottom', '.cyo-preview__slot-item--top']) {
      const item = slot.querySelector(cls);
      if (item) {
        item.removeAttribute('src');
        item.alt = '';
      }
    }
    slot.setAttribute('data-filled', 'false');
    slot.setAttribute('data-has-top', 'false');
  }

  /**
   * Grid column count, read from the preview grid's `--cyo-cols` (set in Liquid).
   * Falls back to the same rule the Liquid uses: 3 columns for boxes of 6 or
   * fewer slots, otherwise 4.
   *
   * @param {Element} preview
   * @param {number} slotCount
   * @returns {number}
   */
  #gridColumns(preview, slotCount) {
    const grid = preview.querySelector('.cyo-preview__grid');
    const raw = grid ? parseInt(getComputedStyle(grid).getPropertyValue('--cyo-cols'), 10) : NaN;
    if (Number.isInteger(raw) && raw > 0) return raw;
    return slotCount <= 6 ? 3 : 4;
  }

  /**
   * Reorder a row-major per-slot array so each distinct flavour occupies whole
   * columns when that lays out more cleanly than whole rows (e.g. 4 flavours in a
   * 4×3 grid → one column each). Returns the array unchanged when row-major
   * already fills whole rows (e.g. 3 flavours → one row each), or when neither
   * orientation divides evenly (best-effort fallback for awkward counts).
   *
   * @param {Array<{image: string, title: string} | undefined>} arr
   * @param {number} cols
   * @returns {Array<{image: string, title: string} | undefined>}
   */
  #organizeLayout(arr, cols) {
    const slotCount = arr.length;
    if (!cols || cols < 1 || slotCount % cols !== 0) return arr;

    const distinct = new Set(arr.filter(Boolean).map((item) => item.image));
    const n = distinct.size;
    if (n < 1 || slotCount % n !== 0) return arr;

    const rows = slotCount / cols;
    const perFlavor = slotCount / n;

    // Row-major already fills whole rows — leave it as is.
    if (perFlavor % cols === 0) return arr;

    // Column-major fills whole columns — remap into DOM (row-major) positions.
    if (perFlavor % rows === 0) {
      const out = new Array(slotCount);
      for (let k = 0; k < slotCount; k++) {
        const col = Math.floor(k / rows);
        const row = k % rows;
        out[row * cols + col] = arr[k];
      }
      return out;
    }

    return arr;
  }

  /**
   * Update the CYO preview grid in the gallery with current selections.
   */
  #updatePreview() {
    const root = this.#section || document;
    const preview = root.querySelector('[data-cyo-preview]');
    if (preview) {
      const slots = preview.querySelectorAll('[data-cyo-slot]');
      if (slots.length > 0) {
        let { bottom, top } = this.#buildLayers(slots.length);
        // Arrange each layer into whole rows or whole columns (whichever divides
        // the grid evenly) so the box reads as organized. Applied to both layers
        // so combination's top + bottom each organize independently; `top` is
        // empty in single mode, so the call is a no-op there. Skipped for
        // segmented boxes (Dessert Board), where each step already owns a fixed
        // slot range and must not be reordered across the whole grid.
        if (!this.#isSegmented()) {
          const cols = this.#gridColumns(preview, slots.length);
          bottom = this.#organizeLayout(bottom, cols);
          top = this.#organizeLayout(top, cols);
        }
        for (let i = 0; i < slots.length; i++) {
          if (bottom[i]) {
            this.#fillSlot(slots[i], bottom[i], top[i]);
          } else {
            this.#emptySlot(slots[i]);
          }
        }
      }
    }

    this.#applyClearVisibility();
  }

  /**
   * Variant change started: show the same faded / wait-cursor "updating" state
   * the variant picker and Add to Cart use, until the update completes.
   *
   * @type {EventListener}
   */
  #handleVariantSelected = () => {
    this.dataset.variantUpdating = 'true';
    this.setAttribute('aria-busy', 'true');
  };

  /**
   * Reset the builder once its box has been added to the cart, so it starts
   * fresh for the next box. Ignores failed cart operations; no-op when nothing
   * is selected.
   *
   * @type {EventListener}
   */
  #handleCartUpdate = (event) => {
    if (event instanceof CustomEvent && event.detail?.data?.didError) return;
    if (this.#hasSelection()) this.#clearSelections();
  };

  /**
   * On variant change, re-render this builder from the fetched page HTML
   * (matched by data-block-id) so the correct steps/layout for the new variant
   * show, then re-init — which clears the selection. Mirrors the
   * `variant-rich-text-metafield` pattern; the variant picker is never touched.
   *
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    const data = event.detail?.data;
    if (!data) return;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const newBuilder = data.html?.querySelector(`cyo-builder[data-block-id="${this.dataset.blockId}"]`);
    if (!newBuilder) {
      // The new variant has no CYO steps, so no builder is rendered for it. Hide
      // this one instead of leaving the previous variant's steps on screen; it
      // re-shows when a variant that has steps is selected again.
      this.hidden = true;
      // Drop any English lookup still pending for the previous variant.
      this.#logicSeq++;
      delete this.dataset.logicLoading;
      delete this.dataset.variantUpdating;
      this.removeAttribute('aria-busy');
      // Drop the previous variant's selection first: #clearSelections re-runs
      // #serializeCart, which rewrites `data-cyo-payload` empty. Leaving it would
      // let an add-to-cart submit the old variant's CYO children and properties.
      this.#clearSelections();

      // Then release our Add to Cart block — #clearSelections re-applies it (an
      // empty selection is invalid), but this variant needs no selection at all
      // and has no builder on screen to make one, so a block left standing would
      // disable Add to Cart with nothing the shopper could do about it.
      for (const btn of this.#getAtcButtons()) {
        setAtcBlock(btn, ATC_BLOCK.cyo, false);
      }
      return;
    }

    // Copy the host attributes that drive behaviour, then morph the children.
    this.hidden = false;
    this.dataset.totalSlots = newBuilder.dataset.totalSlots || this.dataset.totalSlots;
    this.dataset.selectionType = newBuilder.dataset.selectionType || 'single';
    // Non-English: the fetched HTML is the neutral loading render again — carry its
    // logic-loading state and the NEW variant id, so the English selection type for
    // that variant is read (from the module's cache — no new request) before init.
    this.#logicSeq++;
    for (const key of ['logicLoading', 'productHandle', 'variantId', 'stepCount']) {
      if (newBuilder.dataset[key] != null) this.dataset[key] = newBuilder.dataset[key];
      else delete this.dataset[key];
    }
    morph(this, newBuilder, { childrenOnly: true });

    // Re-init after the media-gallery preview finishes its own re-render, then
    // clear the "updating" state.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#initOrResolve();
        delete this.dataset.variantUpdating;
        this.removeAttribute('aria-busy');
      });
    });
  };

  /**
   * Serialize the current selection into a cart-ready payload on
   * `data-cyo-payload`, so the personalization drawer can read it at add-to-cart
   * without any CYO logic of its own. All values are snapshotted here (immutable
   * once on the cart line), matching the cart-contract shape:
   *   { type: { singular, plural }, count, combinations, children: [{ id: variantId, quantity }] }
   * - type: item-word forms (canonical casing) for the labels,
   *   stored as one cart-line property object (read directly in Liquid)
   * - count: total pieces (single = slots; combination doubles for two layers)
   * - combinations: distinct flavours chosen across all steps
   * - children: one $0 line item per selected flavour, quantity = its slot count
   */
  #serializeCart() {
    /** @type {{ id: number, quantity: number, step: number }[]} */
    const children = [];
    let combinations = 0;

    // Combination boxes stack two layers per slot (bottom = step 1, top = step 2)
    // so the piece count doubles. When the second step is left empty, the top
    // layer mirrors the bottom — step 1 fills BOTH layers — so step 1's child
    // quantities must double too, otherwise the children sum to half the count
    // on the cart line.
    const isCombination = this.#selectionType === 'combination';
    const stepIds = [...this.#selections.keys()];
    const firstStepId = stepIds[0];
    const secondStepFilled = stepIds
      .slice(1)
      .some((id) => (this.#selections.get(id) || []).length > 0);
    const mirrorFirstStep = isCombination && !secondStepFilled;

    // The STEP INDEX is captured here, where it is still known. Downstream the
    // children are a flat list, and nothing can recover which step a selection
    // came from once it is lost — which matters because the trusted projection
    // authorises merchandise per step, not per box.
    const stepIndexById = new Map([...this.#selections.keys()].map((id, i) => [id, i]));

    for (const [stepId, selections] of this.#selections) {
      if (!selections.length) continue;
      combinations += selections.length;
      let quantity = this.#stepQuantities.get(stepId) || this.#totalSlots;
      if (mirrorFirstStep && stepId === firstStepId) quantity *= 2;
      const dist = this.#distributeSlots(selections, quantity);
      selections.forEach((sel, i) => {
        const count = dist[i] ? dist[i].count : 0;
        const variantId = Number(sel.variantId);
        if (count > 0 && variantId) {
          children.push({ id: variantId, quantity: count, step: stepIndexById.get(stepId) });
        }
      });
    }

    // Piece count on the cart line. The Dessert Board (segmented) is driven by
    // its step collections — its total is the sum of the step quantities, not the
    // variant total-slots value (which the board doesn't use for rendering). Single
    // boxes use total-slots; combination doubles it for the two stacked layers.
    let count;
    if (this.#isSegmented()) {
      count = [...this.#stepQuantities.values()].reduce((sum, q) => sum + q, 0);
    } else {
      count = this.#totalSlots * (isCombination ? 2 : 1);
    }

    // Gift wrap — a single $0 child line, quantity 1, on its own key so it stays
    // separate from the flavour `children`. Null when the variant offers no wrap.
    const giftWrap =
      this.#giftWrapSelection && this.#giftWrapSelection.variantId
        ? { id: Number(this.#giftWrapSelection.variantId), quantity: 1 }
        : null;

    this.dataset.cyoPayload = JSON.stringify({
      type: {
        singular: this.#cyoTypeSingular,
        plural: this.#cyoTypePlural,
      },
      count,
      combinations,
      children,
      giftWrap,
    });
  }

  /**
   * Dispatch a custom event with current selection state.
   */
  #dispatchChange() {
    this.#serializeCart();
    this.dispatchEvent(
      new CustomEvent('cyo:selection-changed', {
        detail: {
          selectionType: this.#selectionType,
          selections: this.#getAllSelections(),
        },
        bubbles: true,
      })
    );
  }
}

if (!customElements.get('cyo-builder')) {
  customElements.define('cyo-builder', CyoBuilder);
}
